const BASE_URL = import.meta.env.VITE_API_URL || '';

// ─── Token storage ───
const TOKEN_KEY = 'recon_access_token';
const REFRESH_KEY = 'recon_refresh_token';
const USER_KEY = 'recon_user';

export const tokenStorage = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getUser: () => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  set: (access, refresh, user) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

// ─── Core request with auth + refresh ───

let onUnauthorized = null;  // callback the AuthContext sets up to redirect to /login
export function setOnUnauthorized(fn) {
  onUnauthorized = fn;
}

async function request(path, options = {}, retry = true) {
  const url = `${BASE_URL}${path}`;
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = tokenStorage.getAccess();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && retry) {
    // Try refresh once
    const refreshed = await tryRefresh();
    if (refreshed) return request(path, options, false);
    tokenStorage.clear();
    if (onUnauthorized) onUnauthorized();
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `API error ${res.status}`);
  }

  // Handle 204 / empty responses
  if (res.status === 204) return null;
  return res.json();
}

async function tryRefresh() {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) return false;
  try {
    const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    tokenStorage.set(data.access_token, data.refresh_token, null);
    return true;
  } catch {
    return false;
  }
}

// ─── API surface ───

export const api = {
  get: (path) => request(path),
  post: (path, data) => request(path, { method: 'POST', body: JSON.stringify(data) }),
  put: (path, data) => request(path, { method: 'PUT', body: JSON.stringify(data) }),
  del: (path) => request(path, { method: 'DELETE' }),

  uploadFile: async (path, formData) => {
    const url = `${BASE_URL}${path}`;
    const headers = {};
    const token = tokenStorage.getAccess();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { method: 'POST', body: formData, headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Upload error ${res.status}`);
    }
    return res.json();
  },

  // ─── Auth ───
  login: async (email, password) => {
    const res = await fetch(`${BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || 'Login failed');
    }
    const data = await res.json();
    tokenStorage.set(data.access_token, data.refresh_token, data.user);
    return data;
  },
  logout: async () => {
    try {
      await request('/api/auth/logout', { method: 'POST' });
    } catch {}
    tokenStorage.clear();
  },
  me: () => request('/api/auth/me'),
  changePassword: (current_password, new_password) =>
    request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  // ─── Existing modules (now with shop_id param) ───
  getDashboard: (shopId) => request(shopId ? `/api/dashboard?shop_id=${shopId}` : '/api/dashboard'),
  getModule: (endpoint, shopId) =>
    request(shopId ? `${endpoint}?shop_id=${shopId}` : endpoint),
  getShops: () => request('/api/shops'),
  switchShop: (shopId) => request('/api/shops/switch', { method: 'POST', body: JSON.stringify({ shop_id: shopId }) }),
  getShopsSummary: () => request('/api/shops/summary'),
  getHealth: () => request('/api/health'),
  getAuditTrail: (limit = 50, module = null) => {
    const params = new URLSearchParams({ limit });
    if (module) params.set('module', module);
    return request(`/api/audit-trail?${params}`);
  },
  updateCreditStatus: (returnOrderId, data) =>
    request(`/api/credits/${returnOrderId}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  createOverride: (data) => request('/api/overrides', { method: 'POST', body: JSON.stringify(data) }),
  getStatements: () => request('/api/statements'),
  getStatement: (id) => request(`/api/statements/${id}`),

  // ─── Phase C + D: new endpoints ───

  // Module 11 Parts to Return
  getPartsToReturn: (shopId) =>
    request(shopId ? `/api/modules/parts-to-return?shop_id=${shopId}` : '/api/modules/parts-to-return'),
  markPartPickedUp: (stateId, notes) =>
    request(`/api/parts-to-return/${stateId}/mark-picked-up`, { method: 'POST', body: JSON.stringify({ notes }) }),
  voidPart: (stateId, reason) =>
    request(`/api/parts-to-return/${stateId}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // Refund state machine
  updateRefundState: (returnOrderId, data) =>
    request(`/api/credits/${returnOrderId}/refund-state`, { method: 'PUT', body: JSON.stringify(data) }),

  // Exception rules
  listExceptionRules: (shopId) =>
    request(shopId ? `/api/exception-rules?shop_id=${shopId}` : '/api/exception-rules'),
  createExceptionRule: (data) => request('/api/exception-rules', { method: 'POST', body: JSON.stringify(data) }),
  updateExceptionRule: (id, data) => request(`/api/exception-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExceptionRule: (id) => request(`/api/exception-rules/${id}`, { method: 'DELETE' }),

  // Tasks
  listTasks: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/tasks${qs ? '?' + qs : ''}`);
  },
  getTask: (id) => request(`/api/tasks/${id}`),
  createTask: (data) => request('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTaskStatus: (id, data) => request(`/api/tasks/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  reassignTask: (id, data) => request(`/api/tasks/${id}/reassign`, { method: 'PUT', body: JSON.stringify(data) }),
  addTaskComment: (id, comment) => request(`/api/tasks/${id}/comments`, { method: 'POST', body: JSON.stringify({ comment }) }),
  getTaskCounts: (shopId) =>
    request(shopId ? `/api/tasks/summary/counts?shop_id=${shopId}` : '/api/tasks/summary/counts'),

  // Store settings
  getStoreSettings: (shopId) => request(`/api/store-settings/${shopId}`),
  updateStoreSettings: (shopId, data) =>
    request(`/api/store-settings/${shopId}`, { method: 'PUT', body: JSON.stringify(data) }),

  // User management (super_admin only)
  listUsers: () => request('/api/users'),
  createUser: (data) => request('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivateUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
  assignShops: (id, shopIds) =>
    request(`/api/users/${id}/shops`, { method: 'POST', body: JSON.stringify({ shop_ids: shopIds }) }),
  revokeShop: (id, shopId) => request(`/api/users/${id}/shops/${shopId}`, { method: 'DELETE' }),

  // COD offset
  runCodOffsetDetection: (shopId) =>
    request(`/api/cod-offset/detect?shop_id=${shopId}`, { method: 'POST' }),
  listCodOffsetMatches: (status) =>
    request(status ? `/api/cod-offset/matches?status=${status}` : '/api/cod-offset/matches'),
  confirmCodOffset: (id, confirmed, notes) =>
    request(`/api/cod-offset/matches/${id}/confirm`, { method: 'PUT', body: JSON.stringify({ confirmed, notes }) }),

  // Daily audit email
  generateAuditEmailDraft: (shopId, auditDate) => {
    const params = new URLSearchParams({ shop_id: shopId });
    if (auditDate) params.set('audit_date', auditDate);
    return request(`/api/audit-email/generate-draft?${params}`, { method: 'POST' });
  },
  getAuditEmailDraft: (shopId, auditDate) => {
    const params = new URLSearchParams({ shop_id: shopId });
    if (auditDate) params.set('audit_date', auditDate);
    return request(`/api/audit-email/draft?${params}`);
  },
  updateAuditEmailDraft: (draftId, data) =>
    request(`/api/audit-email/draft/${draftId}`, { method: 'PUT', body: JSON.stringify(data) }),
  sendAuditEmailDraft: (draftId, recipients) =>
    request(`/api/audit-email/draft/${draftId}/send`, { method: 'POST', body: JSON.stringify({ recipients }) }),

  // Google Drive
  getDriveConfigStatus: () => request('/api/drive/config-status'),
  scanDrive: (shopNickname, date) =>
    request('/api/drive/scan', { method: 'POST', body: JSON.stringify({ shop_nickname: shopNickname, date }) }),
  getDriveSyncLog: (shopNickname, limit = 50) => {
    const params = new URLSearchParams({ limit });
    if (shopNickname) params.set('shop_nickname', shopNickname);
    return request(`/api/drive/sync-log?${params}`);
  },

  // COD pipeline
  processDayAsync: (shopId, shopNickname, date) =>
    request('/api/cod-drive/process-day-async', {
      method: 'POST',
      body: JSON.stringify({ shop_id: shopId, shop_nickname: shopNickname, date }),
    }),
  reviewCodCheck: (checkId, data) =>
    request(`/api/auditor/cod-checks/${checkId}/review`, { method: 'PUT', body: JSON.stringify(data) }),
  reassignCheckVendor: (checkId, data) =>
    request(`/api/auditor/cod-checks/${checkId}/reassign-vendor`, { method: 'PUT', body: JSON.stringify(data) }),

  // COD checks queue (used by Daily Audit pathway sub-tabs)
  listCodChecks: ({ shopId, status, folderDate, limit = 100 } = {}) => {
    const qs = new URLSearchParams({ limit });
    if (shopId) qs.set('shop_id', shopId);
    if (status) qs.set('audit_status', status);
    if (folderDate) qs.set('folder_date', folderDate);
    return request(`/api/auditor/cod-checks-queue?${qs}`);
  },

  // Edit + re-audit (Daily Audit Phase 3)
  editCodCheck: (checkId, payload) =>
    request(`/api/auditor/cod-checks/${checkId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  editCodCheckInvoice: (invoiceId, payload) =>
    request(`/api/auditor/cod-check-invoices/${invoiceId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  tekmetricRoPartsTotal: (shopId, roNumber) => {
    const qs = new URLSearchParams({ shop_id: shopId, ro_number: roNumber });
    return request(`/api/auditor/tekmetric/ro-parts-total?${qs}`);
  },
  // Apply COD Credit (Phase D)
  listPendingCodCredits: (shopId, vendorProfileId = null) => {
    const qs = new URLSearchParams({ shop_id: shopId });
    if (vendorProfileId) qs.set('vendor_profile_id', vendorProfileId);
    return request(`/api/auditor/cod-credits/pending?${qs}`);
  },
  applyCreditToCheck: (checkId, creditStatusId, amountCents) =>
    request(`/api/auditor/cod-checks/${checkId}/apply-credit`, {
      method: 'POST',
      body: JSON.stringify({ credit_status_id: creditStatusId, amount_cents: amountCents }),
    }),
  // Vendor profile picker (existing endpoint, normalized name)
  listVendorProfiles: (shopId) => {
    const qs = new URLSearchParams();
    if (shopId) qs.set('shop_id', shopId);
    return request(`/api/vendor-profiles${qs.toString() ? '?' + qs : ''}`);
  },
  // Mark passed / ignored (re-uses existing /review endpoint)
  setCodCheckStatus: (checkId, audit_status, notes) =>
    request(`/api/auditor/cod-checks/${checkId}/review`, {
      method: 'PUT',
      body: JSON.stringify({ audit_status, notes }),
    }),

  // Daily Audit consolidated tab. Pass either { days } or { from, to }.
  dailyAuditSummary: (shopId, opts = {}) => {
    const qs = new URLSearchParams({ shop_id: shopId });
    if (opts.from && opts.to) {
      qs.set('from', opts.from);
      qs.set('to', opts.to);
    } else {
      qs.set('days', opts.days || 30);
    }
    return request(`/api/auditor/daily-audit/summary?${qs}`);
  },
  dailyAuditPathwaySummary: (date, pathway, shopId) => {
    const qs = new URLSearchParams({ shop_id: shopId });
    return request(`/api/auditor/daily-audit/${date}/pathway/${pathway}/summary?${qs}`);
  },
  // PDF preview — returns blob URL for an <img> tag (handles auth header)
  fetchDrivePreviewBlob: async (fileId, pageIndex) => {
    const url = `${BASE_URL}/api/drive/preview/${fileId}/page/${pageIndex}`;
    const headers = {};
    const token = tokenStorage.getAccess();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Preview failed');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  // Vendor merge
  mergeVendorProfiles: (sourceId, targetId, mergeAliases = true) =>
    request(`/api/vendor-profiles/${sourceId}/merge-into`, {
      method: 'POST',
      body: JSON.stringify({ target_profile_id: targetId, merge_aliases: mergeAliases }),
    }),

  // Core charge tracking
  listCoreCharges: (shopId, status) => {
    const params = new URLSearchParams();
    if (shopId) params.set('shop_id', shopId);
    if (status) params.set('status', status);
    const qs = params.toString();
    return request(`/api/core-tracking${qs ? '?' + qs : ''}`);
  },
  coreChargeSummary: (shopId) =>
    request(shopId ? `/api/core-tracking/summary?shop_id=${shopId}` : '/api/core-tracking/summary'),
  markCoreReturned: (id, notes) =>
    request(`/api/core-tracking/${id}/mark-returned`, { method: 'PUT', body: JSON.stringify({ notes }) }),
  markCoreCreditReceived: (id, creditMemoReference, notes) =>
    request(`/api/core-tracking/${id}/mark-credit-received`, {
      method: 'PUT',
      body: JSON.stringify({ credit_memo_reference: creditMemoReference, notes }),
    }),
  voidCoreCharge: (id, reason) =>
    request(`/api/core-tracking/${id}/void`, { method: 'PUT', body: JSON.stringify({ reason }) }),

  // Vendor profiles
  createVendorProfile: (data) =>
    request('/api/vendor-profiles', { method: 'POST', body: JSON.stringify(data) }),
  updateVendorProfile: (id, data) =>
    request(`/api/vendor-profiles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  submitVendorProfile: (id) =>
    request(`/api/vendor-profiles/${id}/submit`, { method: 'PUT' }),
  approveVendorProfile: (id) =>
    request(`/api/vendor-profiles/${id}/approve`, { method: 'PUT' }),
  rejectVendorProfile: (id, reason) =>
    request(`/api/vendor-profiles/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  findVendorByName: (shopId, name) =>
    request('/api/vendor-profiles/find-by-name', { method: 'POST', body: JSON.stringify({ shop_id: shopId, name }) }),

  // Document review
  listDocumentReviews: ({ shopId, status = 'PENDING', folderDate, limit = 100 } = {}) => {
    const qs = new URLSearchParams({ status, limit });
    if (shopId) qs.set('shop_id', shopId);
    if (folderDate) qs.set('folder_date', folderDate);
    return request(`/api/document-review/queue?${qs}`);
  },
  confirmDocumentReview: (id, confirmedValue, notes) =>
    request(`/api/document-review/${id}/confirm`, {
      method: 'PUT',
      body: JSON.stringify({ confirmed_value: confirmedValue, notes }),
    }),
  dismissDocumentReview: (id, reason) =>
    request(`/api/document-review/${id}/dismiss`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
  reopenDocumentReview: (id, reason) =>
    request(`/api/document-review/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};
